import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FaUserPlus, FaTrash, FaUsers, FaSearch, FaUserShield, FaClock, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { ClassBranchAdminPage } from "./ClassBranchAdmin";
import { toast, Toaster } from "react-hot-toast";

function AdminPage() {
  const location = useLocation();
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    role: "user" // Varsayılan rol
  });

  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [accessTimeSettings, setAccessTimeSettings] = useState({
    enabled: false,
    start_time: '09:00',
    end_time: '18:00',
    timezone: 'Europe/Istanbul'
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsInitialLoading, setSettingsInitialLoading] = useState(true);
  
  // Admin sayfası ilk yüklendiğinde kullanıcıları çekme
  useEffect(() => {
    const loadData = async () => {
      await fetchUsers();
      await fetchAccessTimeSettings();
    };
    loadData();
  }, []);

  // Admin paneline her gelindiğinde settings'i yeniden yükle
  useEffect(() => {
    if (location.pathname === '/admin') {
      fetchAccessTimeSettings(true); // forceRefresh = true
    }
  }, [location.pathname]);

  const fetchUsers = async () => {
    setLoading(true);
    const token = localStorage.getItem("authToken");

    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(`http://esnakademi.com/api/auth/users?_t=${timestamp}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(response.data);
      setError(null);
    } catch (error) {
      console.error("Kullanıcıları alırken hata oluştu:", error.response?.data || error.message);
      setError("Kullanıcı listesi yüklenemedi. Lütfen daha sonra tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessTimeSettings = async (forceRefresh = false) => {
    const token = localStorage.getItem("authToken");
    
    if (forceRefresh) {
      setSettingsInitialLoading(true);
    }

    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(`http://esnakademi.com/api/settings/access-times?_t=${timestamp}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.data && response.data.data) {
        setAccessTimeSettings(prev => ({
          ...prev,
          ...response.data.data
        }));
      } else {
        // Default ayarları set et
        setAccessTimeSettings({
          enabled: false,
          start_time: '09:00',
          end_time: '18:00',
          timezone: 'Europe/Istanbul'
        });
      }
    } catch (error) {
      console.error("Erişim saati ayarları alınırken hata oluştu:", error.response?.data || error.message);
      // Hata durumunda da default ayarları set et
      setAccessTimeSettings({
        enabled: false,
        start_time: '09:00',
        end_time: '18:00',
        timezone: 'Europe/Istanbul'
      });
      toast.error("Erişim saati ayarları yüklenemedi, varsayılan ayarlar kullanılıyor");
    } finally {
      // Küçük bir delay ekleyerek UI'ın güncellenmesini garantileyelim
      setTimeout(() => {
        setSettingsInitialLoading(false);
      }, 100);
    }
  };

  const updateAccessTimeSettings = async (newSettings) => {
    setSettingsLoading(true);
    const token = localStorage.getItem("authToken");

    try {
      const response = await axios.put(
        "http://esnakademi.com/api/settings/access-times",
        newSettings,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      setAccessTimeSettings(prev => ({
        ...prev,
        ...response.data.data
      }));
      toast.success("Erişim saati ayarları güncellendi");
      
      // Güncelleme sonrası tekrar fetch edelim
      setTimeout(() => {
        fetchAccessTimeSettings(true);
      }, 200);
    } catch (error) {
      console.error("Erişim saati ayarları güncellenirken hata oluştu:", error.response?.data || error.message);
      toast.error("Ayarlar güncellenirken bir hata oluştu");
    } finally {
      setSettingsLoading(false);
    }
  };

  // Yeni kullanıcı ekleme işlemi
  const handleAddUser = async (e) => {
    e.preventDefault();
    
    // Form doğrulama
    if (!newUser.username || !newUser.email || !newUser.password) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }
    
    // Email formatı kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      toast.error("Geçerli bir e-posta adresi girin");
      return;
    }
    
    // Şifre uzunluğu kontrolü
    if (newUser.password.length < 6) {
      toast.error("Şifre en az 6 karakter olmalıdır");
      return;
    }

    const token = localStorage.getItem("authToken");
    
    try {
      const loading = toast.loading("Kullanıcı ekleniyor...");
      
      await axios.post(
        "http://esnakademi.com/api/auth/register",
        JSON.stringify(newUser),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.dismiss(loading);
      toast.success("Kullanıcı başarıyla eklendi!");
      
      // Formu sıfırla
      setNewUser({ username: "", email: "", password: "", role: "user" });
      
      // Kullanıcı listesini güncelle
      fetchUsers();
    } catch (error) {
      console.error("Yeni kullanıcı eklerken bir hata oluştu:", error);
      toast.error(error.response?.data?.message || "Kullanıcı eklenirken bir hata oluştu!");
    }
  };

  const handleDeleteUser = async (id, username) => {
    if (!window.confirm(`"${username}" kullanıcısını silmek istediğinize emin misiniz?`)) {
      return;
    }
    
    const token = localStorage.getItem("authToken");

    try {
      const loading = toast.loading("Kullanıcı siliniyor...");
      
      await axios.delete(
        `http://esnakademi.com/api/auth/users?id=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.dismiss(loading);
      toast.success("Kullanıcı başarıyla silindi");
      
      // Kullanıcı listesini güncelle
      fetchUsers();
    } catch (error) {
      console.error("Kullanıcı silinirken hata oluştu:", error.response?.data || error.message);
      toast.error("Kullanıcı silinemedi. Lütfen daha sonra tekrar deneyin.");
    }
  };

  // Yetki düzenleme işlemi
  

  // Filtrelenmiş kullanıcı listesi
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Kullanıcı rolü rengini belirleme
  const getRoleColor = (role) => {
    switch(role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'teacher':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <Toaster position="top-right" />
      
      <div className="max-w-7xl mx-auto">
        {/* Sayfa Başlığı */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 pb-4 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <FaUserShield className="mr-3 text-purple-600" />
            Yetkili İşlemleri
          </h1>
          <div className="mt-4 md:mt-0 relative">
            <input
              type="text"
              placeholder="Kullanıcı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full md:w-64"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
        </div>

        {/* Ana Bölümler */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Kullanıcı Listesi */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-lg rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <FaUsers className="mr-2" /> Kullanıcı Listesi
                </h2>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                  {error}
                </div>
              )}

              {loading && users.length === 0 ? (
                <div className="flex justify-center items-center p-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
                </div>
              ) : filteredUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kullanıcı</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-purple-800 font-semibold text-sm">{user.username.charAt(0).toUpperCase()}</span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{user.username}</div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(user.role)}`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                           
                            <button
                              onClick={() => handleDeleteUser(user.id, user.username)}
                              className="ml-2 text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                              title="Kullanıcıyı Sil"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  {searchTerm ? 'Arama kriterlerine uygun kullanıcı bulunamadı.' : 'Henüz kullanıcı bulunmuyor.'}
                </div>
              )}
            </div>
          </div>
          
          {/* Kullanıcı Yönetimi */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-lg rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <FaUserPlus className="mr-2" /> Yeni Kullanıcı Ekle
                </h2>
              </div>
              <div className="p-6">
                <form onSubmit={handleAddUser} className="space-y-4">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">Kullanıcı Adı</label>
                    <input
                      id="username"
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Kullanıcı adı"
                      value={newUser.username}
                      onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                    <input
                      id="email"
                      type="email"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="ornek@mail.com"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Şifre</label>
                    <input
                      id="password"
                      type="password"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="En az 6 karakter"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                    <select
                      id="role"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    >
                      <option value="user">Kullanıcı</option>
                      <option value="Admin">Yönetici</option>
                    </select>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full mt-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:opacity-90 transition-all duration-300 flex items-center justify-center"
                  >
                    <FaUserPlus className="mr-2" /> Kullanıcı Ekle
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        
        {/* Erişim Saati Ayarları */}
        <div className="mt-8">
          <div className="bg-white shadow-lg rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <FaClock className="mr-2" /> Kullanıcı Erişim Saati Ayarları
              </h2>
            </div>
            <div className="p-6">
              {settingsInitialLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                  <span className="ml-3 text-gray-600">Ayarlar yükleniyor...</span>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Erişim Kontrolü Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">Erişim Kontrolü</h3>
                    <p className="text-sm text-gray-500">
                      Kullanıcıların sadece belirli saatlerde giriş yapabilmesi
                    </p>
                  </div>
                  <button
                    onClick={() => updateAccessTimeSettings({
                      ...accessTimeSettings,
                      enabled: !accessTimeSettings.enabled
                    })}
                    disabled={settingsLoading}
                    className={`p-2 rounded-lg transition-colors ${
                      accessTimeSettings.enabled 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {accessTimeSettings.enabled ? (
                      <FaToggleOn className="text-3xl" />
                    ) : (
                      <FaToggleOff className="text-3xl" />
                    )}
                  </button>
                </div>

                {/* Başlangıç Saati */}
                <div className={`space-y-2 ${!accessTimeSettings.enabled ? 'opacity-50' : ''}`}>
                  <label className="block text-sm font-medium text-gray-700">
                    Başlangıç Saati
                  </label>
                  <input
                    type="time"
                    value={accessTimeSettings.start_time}
                    onChange={(e) => setAccessTimeSettings({
                      ...accessTimeSettings,
                      start_time: e.target.value
                    })}
                    disabled={!accessTimeSettings.enabled || settingsLoading}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>

                {/* Bitiş Saati */}
                <div className={`space-y-2 ${!accessTimeSettings.enabled ? 'opacity-50' : ''}`}>
                  <label className="block text-sm font-medium text-gray-700">
                    Bitiş Saati
                  </label>
                  <input
                    type="time"
                    value={accessTimeSettings.end_time}
                    onChange={(e) => setAccessTimeSettings({
                      ...accessTimeSettings,
                      end_time: e.target.value
                    })}
                    disabled={!accessTimeSettings.enabled || settingsLoading}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
              </div>

              {/* Kaydet Butonu */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => updateAccessTimeSettings(accessTimeSettings)}
                  disabled={settingsLoading}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center ${
                    settingsLoading
                      ? 'bg-orange-400 cursor-not-allowed text-white'
                      : 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:opacity-90'
                  }`}
                >
                  {settingsLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Güncelleniyor...
                    </>
                  ) : (
                    <>
                      <FaClock className="mr-2" />
                      Ayarları Kaydet
                    </>
                  )}
                </button>
              </div>

              {/* Bilgi Notu */}
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Bilgi</h3>
                    <p className="mt-1 text-sm text-blue-700">
                      • Admin kullanıcılar her zaman sisteme giriş yapabilir
                      <br />
                      • Ayarlar sadece "user" rolündeki kullanıcıları etkiler
                      <br />
                      • Gece yarısını geçen aralıklar desteklenir (örn: 22:00 - 06:00)
                      <br />
                      • Mevcut saat: {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      <br />
                      {accessTimeSettings.enabled && (
                        <>
                          <strong>Ayarlanan aralık:</strong> {accessTimeSettings.start_time} - {accessTimeSettings.end_time}
                          {parseInt(accessTimeSettings.start_time.split(':')[0]) > parseInt(accessTimeSettings.end_time.split(':')[0]) && 
                            <span className="text-orange-600"> (Gece yarısını geçen aralık)</span>
                          }
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Sınıf & Şube Yönetimi */}
        <div className="mt-8">
          <div className="bg-white shadow-lg rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                </svg>
                Sınıf & Şube Yönetimi
              </h2>
            </div>
            <div className="p-6">
              <ClassBranchAdminPage />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
